export const NUTRITION_TOUCH_DRAG_START_DELAY_MS = 200;
export const NUTRITION_TOUCH_DRAG_START_TOLERANCE_PX = 8;
export const NUTRITION_MOUSE_DRAG_START_DISTANCE_PX = 5;

export function isNutritionTouchDragStartEvent(event) {
  if (!event) {
    return false;
  }

  if (event.type === "touchstart") {
    return true;
  }

  return event.type === "pointerdown" && event.pointerType === "touch";
}

export function hasNutritionTouchDragExceededTolerance({
  startCoordinates,
  currentCoordinates,
  tolerancePx = NUTRITION_TOUCH_DRAG_START_TOLERANCE_PX,
} = {}) {
  if (!startCoordinates || !currentCoordinates) {
    return false;
  }

  const deltaX = Number(currentCoordinates.x) - Number(startCoordinates.x);
  const deltaY = Number(currentCoordinates.y) - Number(startCoordinates.y);
  const tolerance = Math.max(0, Number(tolerancePx) || 0);

  return deltaX * deltaX + deltaY * deltaY > tolerance * tolerance;
}

export function createNutritionTouchDragActivationGate({
  initialCoordinates,
  pointerId = null,
  onActivate,
  delayMs = NUTRITION_TOUCH_DRAG_START_DELAY_MS,
  tolerancePx = NUTRITION_TOUCH_DRAG_START_TOLERANCE_PX,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) {
  let status = "pending";
  let latestCoordinates = initialCoordinates ?? null;
  let timeoutId = setTimeoutFn(() => {
    if (status !== "pending") {
      return;
    }

    status = "activated";
    timeoutId = null;
    onActivate?.(latestCoordinates);
  }, delayMs);

  const clearTimer = () => {
    if (timeoutId == null) {
      return;
    }

    clearTimeoutFn(timeoutId);
    timeoutId = null;
  };

  const matchesPointer = (eventPointerId) =>
    !Number.isFinite(pointerId) ||
    !Number.isFinite(eventPointerId) ||
    pointerId === eventPointerId;

  return {
    getStatus() {
      return status;
    },

    updateCoordinates(nextCoordinates, eventPointerId) {
      if (status !== "pending" || !matchesPointer(eventPointerId) || !nextCoordinates) {
        return false;
      }

      latestCoordinates = nextCoordinates;

      if (
        hasNutritionTouchDragExceededTolerance({
          startCoordinates: initialCoordinates,
          currentCoordinates: nextCoordinates,
          tolerancePx,
        })
      ) {
        status = "canceled";
        clearTimer();
        return true;
      }

      return false;
    },

    cancel(eventPointerId) {
      if (status !== "pending" || !matchesPointer(eventPointerId)) {
        return false;
      }

      status = "canceled";
      clearTimer();
      return true;
    },

    dispose() {
      clearTimer();
    },
  };
}

